import { ethers } from 'ethers';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Linking } from 'react-native';
import { CHAIN_IDS, NETWORKS } from '../type';

declare global {
  interface Window {
    ethereum?: any;
  }
}
export const useWallet = () => {
  const [provider, setProvider] =
    useState<ethers.providers.Web3Provider | null>(null);
  const [account, setAccount] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [chainId, setChainId] = useState<number>(0);
  const [loadingChain, setLoadingChain] = useState<boolean>(false);

  const ensureProvider =
    useCallback((): ethers.providers.Web3Provider | null => {
      if (!window.ethereum) {
        setStatus('MetaMask not found. Please install MetaMask.');
        console.log('=== MetaMask not found');
        // Open metamask deep link
        setTimeout(() => {
          const current =
            typeof window !== 'undefined' ? window.location : null;
          if (!current) {
            return;
          }
          const clean = current.href.replace(/^https?:\/\//, '');
          const metamaskDeepLink = `https://metamask.app.link/dapp/${clean}`;
          Linking.openURL(metamaskDeepLink);
        }, 200);
        return null;
      }
      const web3Provider = new ethers.providers.Web3Provider(
        window.ethereum,
        'any'
      );
      setProvider(web3Provider);
      return web3Provider;
    }, []);

  const connectWallet = useCallback(async (): Promise<void> => {
    try {
      const web3Provider = ensureProvider();
      if (!web3Provider) return;
      const accounts: string[] = await web3Provider.send(
        'eth_requestAccounts',
        []
      );
      const network = await web3Provider.getNetwork();
      setAccount(accounts[0] || '');
      setChainId(network.chainId);
      console.log('=== Connected account', accounts[0]);
      console.log('=== Connected chainId', network.chainId);
      setStatus('Connected');
    } catch (err: any) {
      console.log('=== Connect error', err);
      setStatus(err?.message || 'Failed to connect');
    }
  }, [ensureProvider]);

  const switchOrAddChain = useCallback(
    async (chainId: CHAIN_IDS): Promise<number> => {
      setLoadingChain(true);
      const target = NETWORKS[chainId];
      const web3Provider = ensureProvider();
      if (!web3Provider) {
        setLoadingChain(false);
        console.error('MetaMask not available');
        return 0;
      }
      const {
        chainIdHex,
        chainName,
        nativeCurrency,
        rpcUrls,
        blockExplorerUrls,
      } = target;
      try {
        await web3Provider.send('wallet_switchEthereumChain', [
          { chainId: chainIdHex },
        ]);
        console.log('=== Switched chain to', chainIdHex);
      } catch (switchError: any) {
        setLoadingChain(false);
        if (switchError?.code === 4902) {
          console.log('=== Chain not found, adding', chainIdHex);
          await web3Provider.send('wallet_addEthereumChain', [
            {
              chainId: chainIdHex,
              chainName,
              nativeCurrency,
              rpcUrls,
              blockExplorerUrls,
            },
          ]);
        } else {
          console.log('=== Switch chain error', switchError);
          throw switchError;
        }
      }
      setLoadingChain(false);
      const network = await web3Provider.getNetwork();
      setChainId(network.chainId);
      return network.chainId;
    },
    [ensureProvider]
  );

  // Initialize provider and attach listeners
  useEffect(() => {
    const web3Provider = ensureProvider();
    if (!web3Provider) return;

    const handleAccountsChanged = (accs: string[]) => {
      console.log('=== accountsChanged', accs);
      setAccount(accs?.[0] || '');
    };
    const handleChainChanged = (hex: string | number) => {
      console.log('=== chainChanged', hex);
      const parsed = typeof hex === 'string' ? parseInt(hex, 16) : Number(hex);
      setChainId(parsed);
    };

    window.ethereum?.on?.('accountsChanged', handleAccountsChanged);
    window.ethereum?.on?.('chainChanged', handleChainChanged);

    // Try eager connection
    (async () => {
      try {
        const accounts = await web3Provider.listAccounts();
        const network = await web3Provider.getNetwork();
        if (accounts?.length) {
          setAccount(accounts[0]);
        }
        setChainId(network.chainId);
        console.log('=== Eager chainId', network.chainId);
      } catch (e) {
        // ignore
      }
    })();

    return () => {
      window.ethereum?.removeListener?.(
        'accountsChanged',
        handleAccountsChanged
      );
      window.ethereum?.removeListener?.('chainChanged', handleChainChanged);
    };
  }, [ensureProvider]);

  return {
    provider,
    account,
    status,
    chainId,
    loadingChain,
    connectWallet,
    switchOrAddChain,
    ensureProvider,
  };
};
